import { useEffect, type ChangeEvent } from "react";
import Redo from "@mui/icons-material/Redo";
import Undo from "@mui/icons-material/Undo";
import { Alert, Button, Stack, TextField, Typography } from "@mui/material";
import { useAppDialog } from "@pepsico/layout";
import { usePagePrefs } from "@pepsico/pref-permissions";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { noop } from "es-toolkit";

import {
  getGetGrowersGeoByLocationQueryKey,
  useCreateGrowerGeo,
  useGetGrowersGeoByLocation,
  useUpdateGrowerGeoBoundary,
} from "api/growers-geo/growers-geo";
import type { GrowerGeoResponse } from "api/model";
import { isPlotMapLayerVisible } from "components/PlotMap/utils/layerPrefs";
import { usePlotMap } from "context/PlotMap";
import {
  doesBoundaryOverlapAny,
  fromBoundaryMultiPolygon,
  hasBoundaryGeoJson,
  isBoundarySelfIntersecting,
  toBoundaryMultiPolygon,
} from "context/PlotMap/boundaryUtils";
import { getApiErrorMessage } from "utils";

const getCreateGrowerBoundaryError = getApiErrorMessage(
  "Failed to create grower boundary.",
);

const getUpdateGrowerBoundaryError = getApiErrorMessage(
  "Failed to update grower boundary.",
);

interface GrowerBoundaryEditorProps {}

const isEditableKeyboardTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tagName = target.tagName;
  return (
    tagName === "INPUT" ||
    tagName === "TEXTAREA" ||
    tagName === "SELECT" ||
    target.isContentEditable
  );
};

const GrowerBoundaryEditor: React.FC<GrowerBoundaryEditorProps> = () => {
  const { setAppDialog } = useAppDialog();
  const queryClient = useQueryClient();
  const [prefs] = usePagePrefs();
  const {
    boundary: {
      acceptSaved,
      canRedo,
      canUndo,
      cancelDraft,
      clear,
      editingGrowerGeoId,
      isClosed,
      isDirty,
      isGrowerBoundaryMode,
      label: boundaryLabel,
      points,
      redo,
      setLabel: setBoundaryLabel,
      undo,
    },
  } = usePlotMap();

  const locationUuid = (prefs.location as string | null | undefined) ?? "";
  const { data: growers = [] } = useGetGrowersGeoByLocation(
    { location_uuid: locationUuid },
    {
      query: {
        enabled: locationUuid !== "",
        select: (res): GrowerGeoResponse[] =>
          res.status === 200 ? res.data : [],
      },
    },
  );
  const { isPending: isCreating, mutateAsync: createGrowerGeo } =
    useCreateGrowerGeo();
  const { isPending: isUpdating, mutateAsync: updateGrowerGeo } =
    useUpdateGrowerGeoBoundary();
  const isDeleting = useIsMutating({ mutationKey: ["deleteGrowerGeo"] }) > 0;
  const isSaving = isCreating || isDeleting || isUpdating;

  const hasBoundaryLabel = boundaryLabel.trim() !== "";
  const isSelfIntersecting =
    isClosed && points.length >= 3 && isBoundarySelfIntersecting(points);
  const otherGrowerRings = growers.flatMap((grower) => {
    if (
      grower.id === editingGrowerGeoId ||
      grower.boundary == null ||
      !hasBoundaryGeoJson(grower.boundary)
    ) {
      return [];
    }
    const ring = fromBoundaryMultiPolygon(grower.boundary);
    return ring == null ? [] : [ring];
  });
  const isOverlappingOtherGrower =
    isClosed &&
    points.length >= 3 &&
    doesBoundaryOverlapAny(points, otherGrowerRings);
  const canSaveBoundary =
    isDirty &&
    isClosed &&
    hasBoundaryLabel &&
    !isSaving &&
    !isSelfIntersecting &&
    !isOverlappingOtherGrower;
  const isGrowerLayerHidden = !isPlotMapLayerVisible(prefs, "bnd");

  useEffect(() => {
    if (!isGrowerBoundaryMode) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isEditableKeyboardTarget(event.target) || isSaving) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        cancelDraft();
        return;
      }

      const hasModifier = event.metaKey || event.ctrlKey;
      if (!hasModifier || event.key.toLowerCase() !== "z") {
        return;
      }

      if (event.shiftKey) {
        if (!canRedo) {
          return;
        }
        event.preventDefault();
        redo();
        return;
      }

      if (!canUndo) {
        return;
      }
      event.preventDefault();
      undo();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    canRedo,
    canUndo,
    cancelDraft,
    isGrowerBoundaryMode,
    isSaving,
    redo,
    undo,
  ]);

  const handleBoundaryLabelChange = (event: ChangeEvent<HTMLInputElement>) => {
    setBoundaryLabel(event.target.value);
  };

  const showSaveErrorDialog = (message: string) => {
    setAppDialog({
      confirmText: "Close",
      message: <>{message}</>,
      onConfirm: noop,
      open: true,
      title: "Error",
    });
  };

  const handleSave = async () => {
    if (
      !isDirty ||
      !isClosed ||
      !hasBoundaryLabel ||
      isSaving ||
      isSelfIntersecting ||
      isOverlappingOtherGrower ||
      locationUuid === ""
    ) {
      return;
    }

    const boundary = toBoundaryMultiPolygon(points);
    if (boundary == null) {
      showSaveErrorDialog("Boundary must be a closed shape before saving.");
      return;
    }

    const label = boundaryLabel.trim();

    try {
      if (editingGrowerGeoId != null) {
        const response = await updateGrowerGeo({
          data: {
            boundary,
            grower_label: label,
          },
          growerGeoId: editingGrowerGeoId,
        });
        if (response.status !== 200) {
          showSaveErrorDialog(getUpdateGrowerBoundaryError(response.data));
          return;
        }
      } else {
        const response = await createGrowerGeo({
          data: {
            boundary,
            grower_label: label,
            location_uuid: locationUuid,
          },
        });
        if (response.status !== 200) {
          showSaveErrorDialog(getCreateGrowerBoundaryError(response.data));
          return;
        }
      }

      await queryClient.invalidateQueries({
        queryKey: getGetGrowersGeoByLocationQueryKey({
          location_uuid: locationUuid,
        }),
      });
      acceptSaved();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to save grower boundary.";
      showSaveErrorDialog(message);
    }
  };

  return (
    <Stack spacing={1.5}>
      {isGrowerLayerHidden && (
        <Alert role="alert" severity="warning">
          Grower Boundary layer is hidden. Show it to see the boundary you're
          editing on the map.
        </Alert>
      )}
      <Typography color="text.secondary" variant="body2">
        {isClosed
          ? "Drag vertices to reshape the boundary, or click an edge to insert a point. Press ESC to cancel."
          : "Click the map to place vertices around the grower's field. Click the initial point to close the shape. Press ESC to cancel."}
      </Typography>
      {isSelfIntersecting && (
        <Typography color="error" role="alert" variant="body2">
          Boundary cannot cross itself.
        </Typography>
      )}
      {isOverlappingOtherGrower && (
        <Typography color="error" role="alert" variant="body2">
          Boundary cannot overlap another grower.
        </Typography>
      )}
      <TextField
        aria-label="Boundary label"
        disabled={isSaving}
        error={!hasBoundaryLabel}
        fullWidth
        helperText={hasBoundaryLabel ? undefined : "Boundary label is required"}
        label="Boundary label"
        onChange={handleBoundaryLabelChange}
        required
        size="small"
        value={boundaryLabel}
      />
      <Stack direction="row" spacing={1}>
        <Button
          aria-label="Undo boundary edit"
          disabled={!canUndo || isSaving}
          onClick={undo}
          size="small"
          startIcon={<Undo />}
          variant="text"
        >
          Undo
        </Button>
        <Button
          aria-label="Redo boundary edit"
          disabled={!canRedo || isSaving}
          onClick={redo}
          size="small"
          startIcon={<Redo />}
          variant="text"
        >
          Redo
        </Button>
        <Button
          aria-label="Clear boundary"
          disabled={points.length === 0 || isSaving}
          onClick={clear}
          size="small"
          variant="text"
        >
          Clear
        </Button>
        <Button
          aria-label="Cancel boundary edit"
          color="error"
          disabled={isSaving}
          onClick={cancelDraft}
          size="small"
          variant="text"
        >
          Cancel
        </Button>
        <Button
          aria-label="Save boundary"
          disabled={!canSaveBoundary}
          loading={isSaving}
          onClick={() => {
            void handleSave();
          }}
          size="small"
          variant="text"
        >
          {isSaving ? "Saving…" : "Save"}
        </Button>
      </Stack>
    </Stack>
  );
};

export default GrowerBoundaryEditor;
